import React from 'react';
import Card from '../../../Presentation/components/ui/Card';
import {AppTitle, AppText, AppSmallText, AppBodyText, AppCardTitle} from '../../../Presentation/components/ui/Typography';
import {VisitTrends} from '../models/visit.model';
import {
  Calendar,
  CheckCircle,
  TrendingUp,
  Store,
  XCircle,
  User,
  BarChart3
} from 'lucide-react';

interface VisitsTrendsCardsProps {
  trends: VisitTrends | null;
  loading?: boolean;
}

const VisitsTrendsCards: React.FC<VisitsTrendsCardsProps> = ({
  trends,
  loading = false
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, index) => (
          <Card key={index} className="p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!trends) {
    return (
      <div className="text-center py-8">
        <AppText className="text-gray-500">لا توجد بيانات اتجاهات متاحة</AppText>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Visits */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <AppSmallText className="text-gray-600 mb-1">إجمالي الزيارات</AppSmallText>
              <AppTitle className="text-2xl font-bold text-brand-600">
                {trends.totalVisits.toLocaleString()}
              </AppTitle>
            </div>
            <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-brand-600" />
            </div>
          </div>
        </Card>

        {/* Pending Visits */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <AppSmallText className="text-gray-600 mb-1">في الانتظار</AppSmallText>
              <AppTitle className="text-2xl font-bold text-yellow-600">
                {trends.pendingVisits.toLocaleString()}
              </AppTitle>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </Card>

        {/* Approved Visits */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <AppSmallText className="text-gray-600 mb-1">موافق عليها</AppSmallText>
              <AppTitle className="text-2xl font-bold text-green-600">
                {trends.approvedVisits.toLocaleString()}
              </AppTitle>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        {/* Rejected Visits */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <AppSmallText className="text-gray-600 mb-1">مرفوضة</AppSmallText>
              <AppTitle className="text-2xl font-bold text-red-600">
                {trends.rejectedVisits.toLocaleString()}
              </AppTitle>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* This Month Visits */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <AppSmallText className="text-gray-600 mb-1">زيارات هذا الشهر</AppSmallText>
            <AppTitle className="text-3xl font-bold text-purple-600">
              {trends.visitsThisMonth.toLocaleString()}
            </AppTitle>
          </div>
          <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </Card>

      {/* Top Stores and Visitors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Stores */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Store className="w-5 h-5 text-brand-600" />
            <AppCardTitle className="font-semibold">أفضل المتاجر</AppCardTitle>
          </div>
          <div className="space-y-3">
            {trends.topStores.slice(0, 5).map((store, index) => (
              <div key={store.storeId} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center">
                    <AppSmallText className="text-brand-600 font-semibold">
                      {index + 1}
                    </AppSmallText>
                  </div>
                  <AppBodyText className="font-medium">{store.storeName}</AppBodyText>
                </div>
                <AppSmallText className="text-gray-600">
                  {store.visitCount} زيارة
                </AppSmallText>
              </div>
            ))}
          </div>
        </Card>

        {/* Top Visitors */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-5 h-5 text-green-600" />
            <AppCardTitle className="font-semibold">أفضل الزوار</AppCardTitle>
          </div>
          <div className="space-y-3">
            {trends.topVisitors.slice(0, 5).map((visitor, index) => (
              <div key={visitor.visitorId} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <AppSmallText className="text-green-600 font-semibold">
                      {index + 1}
                    </AppSmallText>
                  </div>
                  <AppBodyText className="font-medium">{visitor.visitorName}</AppBodyText>
                </div>
                <AppSmallText className="text-gray-600">
                  {visitor.visitCount} زيارة
                </AppSmallText>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Visits by Brand */}
      {trends.visitsByBrand.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            <AppCardTitle className="font-semibold">الزيارات حسب البراند</AppCardTitle>
          </div>
          <div className="space-y-3">
            {trends.visitsByBrand.map((brand, index) => (
              <div key={brand.brandName} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                    <AppSmallText className="text-purple-600 font-semibold">
                      {index + 1}
                    </AppSmallText>
                  </div>
                  <AppBodyText className="font-medium">{brand.brandName}</AppBodyText>
                </div>
                <AppSmallText className="text-gray-600">
                  {brand.count} زيارة
                </AppSmallText>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default VisitsTrendsCards;
